import { Chip, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import moment from "moment";
import React from "react";
import { OrganisationRole as GQLOrganisationRole } from "../../../clients/graphqlTypes.js";
import { useOrganisationRoles } from "../users/useRoles.js";

export const ResourceFilterChips = ({
  roles,
  resourceList,
  resourceOrganisationRoleIds,
  resourceAvailability,
  resourceMaxDistance,
  setResourceList,
  setResourceOrganisationRoleIds,
  setResourceAvailability,
  setResourceMaxDistance,
}: {
  roles: GQLOrganisationRole[];
  resourceList: string[] | null | undefined;
  resourceMaxDistance: number | null | undefined;
  resourceOrganisationRoleIds: string[] | null | undefined;
  resourceAvailability:
    | [moment.Moment | null, moment.Moment | null]
    | null
    | undefined;
  setResourceList: (newValue: string[] | null) => void;
  setResourceMaxDistance: (newValue: number) => void;
  setResourceOrganisationRoleIds: (newValue: string[] | null) => void;
  setResourceAvailability: (
    newValue: [moment.Moment | null, moment.Moment | null] | null
  ) => void;
}) => {
  const { t } = useTranslate(["PlanningModal", "Global"]);
  const { getOrganisationRoleTitle } = useOrganisationRoles();
  const isAvailabilityDefined = Boolean(
    resourceAvailability && (resourceAvailability[0] || resourceAvailability[1])
  );

  if (
    !isAvailabilityDefined &&
    !((resourceMaxDistance ?? 0) > 0) &&
    !(
      resourceOrganisationRoleIds &&
      resourceOrganisationRoleIds.length < roles.length
    ) &&
    !(resourceList && resourceList.length > 0)
  )
    return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      sx={{
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {/* Availability */}
      {isAvailabilityDefined && (
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={
            t("Availability", {
              ns: "PlanningModal",
            }) +
            ": " +
            [
              resourceAvailability?.[0]
                ? t("from {date}", {
                    ns: "Global",
                    date: moment(resourceAvailability[0]).format("DD/MM LT"),
                  })
                : null,
              resourceAvailability?.[1]
                ? t("to {date}", {
                    ns: "Global",
                    date: moment(resourceAvailability[1]).format("DD/MM LT"),
                  })
                : null,
            ]
              .filter(Boolean)
              .join(", ")
          }
          onDelete={() => setResourceAvailability(null)}
        />
      )}
      {/* Max distance */}
      {(resourceMaxDistance ?? 0) > 0 && (
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={
            t("Max distance", {
              ns: "PlanningModal",
            }) +
            ": " +
            t("{number} km", {
              ns: "Global",
              number: resourceMaxDistance ?? 0,
            })
          }
          onDelete={() => setResourceMaxDistance(0)}
        />
      )}
      {/* Organisation roles */}
      {resourceOrganisationRoleIds &&
        resourceOrganisationRoleIds.length < roles.length && (
          <Chip
            size="small"
            variant="outlined"
            color="primary"
            label={
              t("Roles", {
                ns: "PlanningModal",
              }) +
              ": " +
              getOrganisationRoleTitle(
                resourceOrganisationRoleIds.map(
                  roleId => roles.find(r => r.id === roleId)!
                )
              )
            }
            onDelete={() => setResourceOrganisationRoleIds(null)}
          />
        )}
      {/* Team members */}
      {resourceList && resourceList.length > 0 && (
        <Chip
          size="small"
          variant="outlined"
          color="primary"
          label={
            t("Members", {
              ns: "PlanningModal",
            }) +
            ": " +
            t("{selectedCount} selected", {
              ns: "Global",
              selectedCount: resourceList.length,
            })
          }
          onDelete={() => setResourceList(null)}
        />
      )}
    </Stack>
  );
};
